// src/utils/registrarAbono.js
//
// 🕳️ BLOQUE V · EL AGUJERO DE GUSANO
//
// Este es el ÚNICO sitio del programa donde se registra un abono de cliente.
// Lo llaman PagosPendientes.js y (a partir de V2) también Recepcion.js, para
// que un abono se guarde exactamente igual sin importar por dónde entró.
//
// El patrón es el de CrearDocumento, que es el que nunca dio problemas:
//   1. Leer el pedido fresco de la base (nunca confiar en lo que hay en pantalla)
//   2. Agregar el abono al array `abonos` del pedido
//   3. Insertar el movimiento en contabilidad SOLO si no está ya registrado
//   4. Guardar el `movimiento_id` en `abonos_registrados_contabilidad`
//
// ⚠️ NUNCA borrar movimientos aquí. Este ayudante solo agrega.
//
// ⚠️ Si algún día hay que cambiar cómo se identifican los abonos (pendiente
//    P-46: hoy se distinguen por la fecha), este es el único archivo a tocar.

import supabase from "../supabaseClient";

export async function registrarAbonoEnPedido({
  ordenId,
  monto,
  fecha,
  nombreCliente = "",
  nombreUsuario = "Administrador",
  clienteIdRespaldo = null,
  numeroRespaldo = "",
}) {
  const montoNumerico = Number(monto || 0);

  if (!ordenId) throw new Error("registrarAbonoEnPedido: falta el id del pedido");
  if (!fecha) throw new Error("registrarAbonoEnPedido: falta la fecha del abono");
  if (montoNumerico <= 0) throw new Error("registrarAbonoEnPedido: el monto debe ser mayor a 0");

  // 1️⃣ Leer datos FRESCOS de la base
  const { data: ordenFresca, error: errorFresca } = await supabase
    .from("ordenes_pedido")
    .select("abonos, abonos_registrados_contabilidad, numero, cliente_id")
    .eq("id", ordenId)
    .single();

  if (errorFresca) throw errorFresca;

  const abonosActuales = ordenFresca.abonos || [];
  const abonosYaRegistrados = ordenFresca.abonos_registrados_contabilidad || [];
  const nuevoAbono = { valor: montoNumerico, fecha };

  // 2️⃣ Agregar el abono al array del pedido
  const { error: errorUpdate } = await supabase
    .from("ordenes_pedido")
    .update({ abonos: [...abonosActuales, nuevoAbono] })
    .eq("id", ordenId);

  if (errorUpdate) throw errorUpdate;

  // 3️⃣ Registrar en contabilidad, solo si no estaba ya registrado.
  //    ⚠️ El valor se normaliza con Number() a propósito: en la base hay
  //    abonos guardados como texto ("210000") y otros como número (210000).
  //    Compararlos sin normalizar fue la causa del error K1 del 28/07/2026.
  const yaRegistrado = abonosYaRegistrados.find(
    (reg) => Number(reg.valor || 0) === montoNumerico && reg.fecha === fecha
  );

  let nuevoRegistro = null;

  if (!yaRegistrado) {
    const numeroLimpio = (() => {
      const n = String(ordenFresca.numero || numeroRespaldo || "");
      return n.startsWith("OP-") ? n : `OP-${n}`;
    })();

    const { data: movData, error: errorMov } = await supabase
      .from("movimientos_contables")
      .insert([{
        orden_id: ordenId,
        cliente_id: ordenFresca.cliente_id || clienteIdRespaldo,
        fecha,
        tipo: "ingreso",
        monto: montoNumerico,
        descripcion: `Abono de ${nombreCliente} - ${numeroLimpio}`,
        categoria: "Abonos",
        estado: "activo",
        usuario: nombreUsuario || "Administrador",
        fecha_modificacion: null,
      }])
      .select();

    if (errorMov) throw errorMov;

    nuevoRegistro = {
      valor: montoNumerico,
      fecha,
      movimiento_id: movData[0].id,
    };
  }

  // 4️⃣ Guardar la libreta actualizada
  const registroFinal = nuevoRegistro
    ? [...abonosYaRegistrados, nuevoRegistro]
    : abonosYaRegistrados;

  await supabase
    .from("ordenes_pedido")
    .update({ abonos_registrados_contabilidad: registroFinal })
    .eq("id", ordenId);

  return {
    nuevoAbono,
    movimientoId: nuevoRegistro?.movimiento_id || null,
    yaEstabaRegistrado: Boolean(yaRegistrado),
  };
}